import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import moment from "moment";
import { isToday } from "date-fns";

import { Block, H3, Table, Thead, Th, Tr, Td } from "commons";
import { convertIdToReal, format, FORMAT, TWELVE_MONTHS, DAY } from "commons/src/NuDateUtils";
import { formatChartMoney, TRANSACTION_TYPE_COLOURS } from "commons/src/ChartFunctions";
import { InfoPopover, InfoDetail, useInfoPopover } from "commons/src/InfoPopover";

import { getDescription, pages } from "../../components/AccountDetailDescription";

const GAIN_LOSS = "Gain / loss";
const GAIN_LOSS_GROSS = "Gain / loss (gross)";
const GAIN_LOSS_NET = "Gain / loss (net)";
const GAIN_GROSS = "Gain (gross)";
const GAIN_NET = "Gain (net)";
const LOSS_GROSS = "Loss (gross)";
const LOSS_NET = "Loss (net)";
const COMBINED = "Combined";
const TOTAL = "Total";

export function TransactionBreakdown({ account, data, rangeId = TWELVE_MONTHS.id }) {
    const [anchorElement, popoverContent, openInfoPopover, closeInfoPopover] = useInfoPopover();

    const getDateRange = data => {
        const dateRange = convertIdToReal(rangeId);

        const firstTransactionDate = moment(
            Object.values(data.VALUATION_VS_MIMO)?.[0]?.valVsMimo?.[account]?.[0]
                ?.formattedEffectiveDate
        );

        dateRange.startDate =
            firstTransactionDate >= dateRange.startDate
                ? firstTransactionDate
                : dateRange.startDate;

        return dateRange;
    };

    const buildHighLevelBlocks = data =>
        data.table.filter(Boolean).map((values, index) => buildBlock(values, index));

    const buildBlock = (values, index) => {
        const type = Object.keys(values)[0];
        const lowLevelRows = values[type]
            .map((lowlevel, index) => buildTableRow(lowlevel, index, type))
            .filter(Boolean);

        const dates = {
            startDate: format(dateRange.startDate, FORMAT.SLASH_SEPARATED),
            endDate: isToday(dateRange.endDate.toDate())
                ? format(dateRange.endDate.clone().subtract(1, DAY), FORMAT.SLASH_SEPARATED)
                : format(dateRange.endDate, FORMAT.SLASH_SEPARATED)
        };

        return lowLevelRows.length === 0 ? null : (
            <Block key={index}>
                <TypeRow data-testid="type-row">
                    {type === GAIN_LOSS ? (
                        <InfoDetail
                            onClick={e =>
                                openInfoPopover(
                                    e,
                                    getDescription(GAIN_LOSS, {
                                        ...dates,
                                        currentPage: pages.analysis
                                    })
                                )
                            }
                        >
                            {type}
                        </InfoDetail>
                    ) : (
                        type
                    )}
                </TypeRow>

                <TransactionsTable data-testid="transactions-table">
                    <Thead>
                        <Tr>
                            <Th align="left">Transaction type</Th>
                            <Th>Total</Th>
                        </Tr>
                    </Thead>
                    <tbody>{lowLevelRows}</tbody>
                </TransactionsTable>
            </Block>
        );
    };

    const buildTableRow = (lowlevel, index, type) => {
        const lowlevelKey = Object.keys(lowlevel)[0];
        const lowlevelData = lowlevel[lowlevelKey];
        const value = account === COMBINED ? lowlevelData.value : lowlevelData.accountMap[account];

        return type !== GAIN_LOSS && !value ? null : (
            <Row key={index} bold={lowlevelKey === TOTAL} data-testid="transaction-table-row">
                <CenteredTd>
                    <Circle
                        data-testid="colour-circle"
                        color={TRANSACTION_TYPE_COLOURS[lowlevelKey]}
                    />
                    <LowLevelName>{filterReplaceGainAndLoss(lowlevelKey)}</LowLevelName>
                </CenteredTd>
                <Td align="right">{formatChartMoney(value ? value : 0.0)}</Td>
            </Row>
        );
    };

    const filterReplaceGainAndLoss = name => {
        const isGross = name === GAIN_GROSS || name === LOSS_GROSS;
        const isNet = name === GAIN_NET || name === LOSS_NET;

        if (!isGross) {
            return isNet ? GAIN_LOSS_NET : name;
        }

        return GAIN_LOSS_GROSS;
    };

    const transKey = Object.keys(data.TRANSACTION_BREAKDOWN)[0];
    const breakdownData = data.TRANSACTION_BREAKDOWN[transKey];
    const dateRange = getDateRange(data);

    return (
        <div>
            <BreakdownTableDiv>
                <InfoPopover
                    handleClose={closeInfoPopover}
                    anchorEl={anchorElement}
                    content={popoverContent}
                    horizontalPosition="left"
                />
                <Header>Transaction breakdown for {dateRange.display.toLowerCase()}</Header>
                <DateRange>
                    {format(dateRange.startDate, FORMAT.SLASH_SEPARATED)} -{" "}
                    {format(dateRange.endDate, FORMAT.SLASH_SEPARATED)}
                </DateRange>
                {buildHighLevelBlocks(breakdownData)}
            </BreakdownTableDiv>
            {breakdownData.netReturnsText ? (
                <div data-testid="net-returns-text">{breakdownData.netReturnsText}</div>
            ) : null}
        </div>
    );
}

const Header = styled(H3)`
    margin-bottom: 0;
`;

const Row = styled(Tr)`
    td {
        font-weight: ${props => (props.bold ? "bold" : "inherit")};
    }
`;

const TransactionsTable = styled(Table)`
    margin-bottom: 0;

    tr:last-of-type {
        td {
            padding-bottom: 0;
        }
    }
`;

const DateRange = styled.span`
    color: ${props => props.theme.primary};
`;

const BreakdownTableDiv = styled.div`
    position: relative;
    width: 100%;
    margin: 1rem auto 0 auto;
`;

export const Circle = styled.div`
    width: 10px;
    height: 10px;
    margin: 5px 5px;
    border-radius: 50%;
    background-color: ${props => props.color};
`;

export const CenteredTd = styled(Td)`
    display: flex;
    align-items: center;
`;

export const LowLevelName = styled.div`
    margin-left: 5px;
`;

export const TypeRow = styled.div`
    padding-bottom: 10px;
    color: ${props => props.theme.primary};
`;

TransactionBreakdown.propTypes = {
    account: PropTypes.string,
    data: PropTypes.object,
    rangeId: PropTypes.string
};
